import { useState, useEffect, useRef } from 'react';

/**
 * useSessionState — a useState replacement that persists the value to
 * sessionStorage under the given key, so the value survives unmount/remount
 * (e.g. navigating away from the page and back) but resets when the tab is
 * closed.
 *
 * If the `key` changes mid-life (for example because it is account-scoped and
 * the user switched accounts or logged in as a different user), the hook
 * resets the state back to `defaultValue`. The previous account's filters do
 * not carry over and nothing is loaded from the new key's slot — every account
 * change starts from a clean slate.
 *
 * @param {string} key            unique storage key (e.g. 'DataFiles.dataCriteria.37')
 * @param {*} defaultValue        value used when nothing is stored under `key`,
 *                                and also the value the state resets to when
 *                                `key` changes
 * @param {function=} transformOnLoad   optional transform applied to the
 *                                       parsed value before it becomes the
 *                                       initial state (e.g. reset skipCount).
 *                                       Receives the parsed value, returns
 *                                       the value to use. Only runs on initial
 *                                       mount; on key change the value resets
 *                                       to `defaultValue` directly.
 */
export function useSessionState(key, defaultValue, transformOnLoad) {
    const [value, setValue] = useState(() => {
        try {
            const saved = sessionStorage.getItem(key);
            if (saved !== null) {
                const parsed = JSON.parse(saved);
                return transformOnLoad ? transformOnLoad(parsed) : parsed;
            }
        } catch {
            //ignore parse / storage errors and fall through to default
        }
        return defaultValue;
    });

    const previousKeyRef = useRef(key);

    useEffect(() => {
        if (previousKeyRef.current !== key) {
            //Key changed mid-life (e.g. account switched). Reset to the
            //default — callers want a clean slate per account, not a
            //rehydration of whatever the new account previously had.
            previousKeyRef.current = key;
            setValue(defaultValue);
            return;
        }
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch {
            //ignore storage write errors (quota, private mode, etc.)
        }
    }, [key, value]);

    return [value, setValue];
}
